import * as React from 'react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { errorMessage, useCreateItem, type KbProduct } from '@/lib/api/vendor-knowledge';
import {
  Actions,
  FieldHelp,
  FieldLabel,
  FormError,
  InlineForm,
  Quote,
  smallControl,
} from '../kb-bits';
import { KIND_LABEL } from '../kb-logic';
import { TextArea, useModuleOptions } from '../review/review-bits';
import type { PickedPassage } from './capture-view';

/**
 * "Add as item" on a passage a curator selected in a capture (E13-F02 §3,
 * `POST /items`): the statement and where the item is filed. It becomes a
 * proposal: it is numbered only when someone confirms it under To review.
 */
export function AddAsItemForm({
  passage,
  products,
  defaultProductId,
  onDone,
  onCancel,
}: {
  passage: PickedPassage;
  products: KbProduct[];
  defaultProductId: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const create = useCreateItem();
  const modules = useModuleOptions();
  const [statement, setStatement] = React.useState('');
  const [kind, setKind] = React.useState('capability');
  const [productId, setProductId] = React.useState(defaultProductId ?? '');
  const [moduleId, setModuleId] = React.useState('');
  const [area, setArea] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const id = React.useId();

  const submit = () => {
    setError(null);
    create.mutate(
      {
        source_version_id: passage.sourceVersionId,
        segment_id: passage.segmentId,
        start: passage.start,
        end: passage.end,
        kind,
        statement: statement.trim(),
        ...(productId ? { product_id: productId } : {}),
        ...(moduleId ? { library_module_id: moduleId } : {}),
        ...(area.trim() ? { process_area: area.trim() } : {}),
      },
      {
        onSuccess: (out) => {
          toast({
            message: out.hold_reason
              ? 'Added as a proposal. A client name was removed from it, so it waits for a look under To review.'
              : 'Added as a proposal. Confirm it under To review to give it a number.',
          });
          onDone();
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  const label = 'mb-1 block text-meta font-medium text-ink-700';
  return (
    <InlineForm aria-label="Add as item">
      <div className="font-semibold text-ink-900">Add as item</div>
      <Quote text={passage.text} />
      <div className="mt-3">
        <FieldLabel htmlFor={`${id}-s`}>Statement</FieldLabel>
        <TextArea
          id={`${id}-s`}
          autoFocus
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          placeholder="What this passage says about the product"
        />
        <FieldHelp>A number in the statement must appear in the quote.</FieldHelp>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2.5">
        <label className="flex flex-col">
          <span className={label}>Kind</span>
          <select
            className={`${smallControl} pr-7`}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {Object.entries(KIND_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className={label}>Product</span>
          <select
            className={`${smallControl} pr-7`}
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">All products</option>
            {products
              .filter((p) => p.status === 'active')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className={label}>Module</span>
          <select
            className={`${smallControl} max-w-[220px] pr-7`}
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
          >
            <option value="">No module</option>
            {modules.map((c) => (
              <optgroup key={c.category} label={c.category}>
                {c.modules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className={label}>Process area</span>
          <input
            className={`${smallControl} w-[180px]`}
            value={area}
            onChange={(e) => setArea(e.target.value)}
            autoComplete="off"
          />
        </label>
      </div>
      {error ? <FormError>{error}</FormError> : null}
      <Actions>
        <WorkspaceButton
          variant="primary"
          disabled={!statement.trim() || create.isPending}
          onClick={submit}
        >
          Add as item
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
      </Actions>
    </InlineForm>
  );
}
